import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { twMerge } from 'tailwind-merge';
import { Button } from '../Button/Button';
import { Tooltip } from '../Tooltip/Tooltip';
export const FilterTabs = ({ activeTab, tabOptions, onTabChange, onTabRemoval, overrideLabels, className, scrollButtons = true, }) => {
    const handleTabClick = (tabKey) => {
        onTabChange(tabKey);
    };
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const scrollRef = useRef(null);
    const scrollTo = (scrollOffset) => {
        if (!scrollRef?.current)
            return;
        const { scrollWidth, clientWidth } = scrollRef.current;
        let targetScrollLeft;
        switch (scrollOffset) {
            case 'start':
                targetScrollLeft = 0;
                setCanScrollRight(scrollWidth > clientWidth);
                setCanScrollLeft(false);
                break;
            case 'end':
                targetScrollLeft = scrollWidth - clientWidth;
                setCanScrollRight(false);
                setCanScrollLeft(true);
                break;
            default:
                targetScrollLeft = scrollOffset;
        }
        const startScrollLeft = scrollRef.current.scrollLeft;
        const distance = targetScrollLeft - startScrollLeft;
        const duration = 300; // Duration of the animation in milliseconds
        let startTime = null;
        const animation = (currentTime) => {
            if (startTime === null)
                startTime = currentTime;
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1); // Ensure progress never exceeds 1
            scrollRef.current.scrollLeft = startScrollLeft + distance * progress;
            if (progress < 1)
                window?.requestAnimationFrame(animation);
        };
        window?.requestAnimationFrame(animation);
    };
    // Handles changing the state of the scroll buttons / scrollbar when the user scrolls
    useEffect(() => {
        const handleScroll = () => {
            if (scrollRef?.current) {
                const { scrollWidth, clientWidth, scrollLeft } = scrollRef.current;
                setCanScrollLeft(scrollLeft > 0);
                setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
            }
        };
        if (scrollRef?.current) {
            handleScroll();
            const currentRef = scrollRef.current;
            currentRef.addEventListener('scroll', handleScroll);
            return () => {
                currentRef.removeEventListener('scroll', handleScroll);
            };
        }
        return undefined; // Ensure useEffect always returns something
    }, [scrollRef?.current?.scrollWidth]);
    return (_jsxs("div", { className: "flex gap-2 overflow-x-auto", children: [scrollButtons && (canScrollLeft || canScrollRight) && (_jsx(Button, { className: "text-primary-400 disabled:cursor-not-allowed disabled:text-gray-400", onClick: () => scrollTo('start'), disabled: !canScrollLeft, children: _jsx(Tooltip, { text: canScrollLeft ? 'Scroll to Start' : '', delay: 1000, children: _jsx(ChevronLeftIcon, { className: "size-5" }) }) })), _jsx("div", { className: twMerge('flex flex-1 gap-1 overflow-x-auto pt-px', className), ref: scrollRef, children: Object.keys(tabOptions).map((key) => (_jsx("div", { onClick: () => handleTabClick(key), className: `flex cursor-pointer gap-1 rounded-t px-3 pb-2.5 pt-1.5 ${activeTab === key
                        ? 'border-primary-300 border border-b-0 border-b-neutral-50 bg-neutral-50'
                        : 'border-grey-300 border border-b-0 bg-zinc-100'}`, children: _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("div", { className: "whitespace-nowrap text-xs font-semibold uppercase leading-tight tracking-wide text-gray-600", children: overrideLabels ? overrideLabels[key] : tabOptions[key] }), onTabRemoval && (_jsx(Button, { className: "btn-icon size-4", onClick: (e) => {
                                    onTabRemoval(key);
                                    e.stopPropagation();
                                }, children: _jsx(XMarkIcon, { className: "size-4 text-gray-400" }) }))] }) }, key))) }), scrollButtons && (canScrollLeft || canScrollRight) && (_jsx(Button, { className: "text-primary-400 disabled:cursor-not-allowed disabled:text-gray-400", onClick: () => scrollTo('end'), disabled: !canScrollRight, children: _jsx(Tooltip, { text: canScrollRight ? 'Scroll to End' : '', delay: 1000, children: _jsx(ChevronRightIcon, { className: "size-5" }) }) }))] }));
};
//# sourceMappingURL=filter-tabs.js.map